import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layers, Plus } from 'lucide-react';
import { Button } from '@siliconx/shared/ui/Button';
import { Select } from '@siliconx/shared/ui/Select';
import { api, throwIfNotOk, unwrap } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-1075 — "Add asset-class line" control for the purchase-analysis
 * builder (design spec §2 "Asset-class lines"). A user adds a line for a
 * whole asset class (e.g. 2U servers) instead of a specific SKU, for
 * early-stage bids before a catalog SKU is picked.
 *
 * Sources the taxonomy from `GET /api/client/reference` — the same full,
 * always-available `assetClasses` list the org-profile catalog-preference
 * picker reads (`orgFeature: null`, no ownership filter: a buyer can bid on
 * a class before owning anything in it). Deliberately NOT
 * `owned-asset-classes` (SIL-1073's ownership-scoped counterpart) — a
 * purchase analysis is about what to BUY, not what the org already holds.
 *
 * Kept as its own component/file (mirrors `LotPicker.tsx`) to minimize
 * merge surface with the parallel SIL-1076 (CSV export) branch, which also
 * touches the builder page.
 */
export function AddClassLineButton({
  analysisId,
  onAdded,
}: {
  analysisId: string;
  onAdded: () => void;
}) {
  const [assetClassId, setAssetClassId] = useState('');
  const [adding, setAdding] = useState(false);

  const referenceQ = useQuery<{ assetClasses: { id: string; name: string }[] }>({
    queryKey: qk.reference(),
    queryFn: async () => unwrap(await api.api.client.reference.$get()),
  });

  const assetClasses = referenceQ.data?.assetClasses ?? [];

  async function handleAdd() {
    if (!assetClassId) return;
    setAdding(true);
    try {
      const res = await api.api.client['purchase-analyses'][':id'].lines.$post({
        param: { id: analysisId },
        json: {
          lineType: 'asset_class',
          assetClassId,
          conditionBand: 'normal_enterprise_wear',
          quantity: 1,
          unitAcquisitionCostUsd: 0,
        },
      });
      await throwIfNotOk(res);
      onAdded();
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        aria-label="Asset class to add"
        value={assetClassId}
        onChange={(e) => setAssetClassId(e.target.value)}
        disabled={referenceQ.isLoading}
        className="w-48"
      >
        <option value="">
          {referenceQ.isLoading ? 'Loading classes…' : 'Select asset class…'}
        </option>
        {assetClasses.map((ac) => (
          <option key={ac.id} value={ac.id}>
            {ac.name}
          </option>
        ))}
      </Select>
      <Button type="button" variant="secondary" onClick={() => void handleAdd()} disabled={!assetClassId || adding}>
        {adding ? <Plus className="mr-1 size-4 animate-pulse" /> : <Layers className="mr-1 size-4" />}
        {adding ? 'Adding…' : 'Add class line'}
      </Button>
    </div>
  );
}
